﻿import '../css/geographic-trends.page.css';

import $ from 'jquery';
import Highcharts from 'highcharts';
import Exporting from 'highcharts/modules/exporting';
Exporting(Highcharts);
import Map from 'highcharts/modules/map';
Map(Highcharts);
import Accessibility from 'highcharts/modules/accessibility';
Accessibility(Highcharts);
import NoData from 'highcharts/modules/no-data-to-display';
NoData(Highcharts);
import usMap from '@highcharts/map-collection/countries/us/us-all.geo.json';
import msaMap from '../map/us-msa.geo.json';
import { appConstants } from './app-constants';
import { configureCharts } from './chart-config';
import { showLoadingSpinner, hideLoadingSpinner } from './loading';
import { getAnalytics, logEvent } from "firebase/analytics";
import { Tooltip } from 'bootstrap';
import CustomSelect from './custom-select';
import RequestManager from './request-manager';
import { showNoData, buildChartColors, sortCategoryItems } from './chart-utils';

const mapType = {
    rate: 1,
    perCapita: 2
};

const geographyType = {
    msa: 1,
    state: 2,
};

const nationalStateLabel = 'US';

class GeographicTrendsPage {

    filters = {
        state: 0,
        vaccine: 0,
        ageGroup: 0,
        gender: 0,
        ethnicity: 0,
        timePeriod: 0,
        timePeriod2: 0, //comparison timeperiod
        map: 0,
        geoType: 0
    }

    mapData = {
        1: [],
        2: []
    };

    viewModel = null;

    mapData = [];

    stateLabel = nationalStateLabel;

    requestManager = new RequestManager();

    constructor() {
        // View model is initialized on script for page
        this.viewModel = viewModel;
        this.init();
    }

    init() {
        configureCharts(Highcharts);
        this.clearFilterUrl();
        this.setDefaultFilters();
        $('#collapseFilters select').attr('autocomplete', 'off');
        this.resetSelectsToDefault();

        this.addFilterChangeHandlers();
        this.addViewMapHandler();
        this.addResetMapHandler();
        this.addChartsToggleHandler();
        this.initializeTooltips();
        this.initializeCustomSelects();
        this.resetSelectsToDefault();
        this.updateViewMapButton();
    }

    initializeTooltips() {
        new Tooltip(document.getElementById('geoTypeFilterTooltip'));
        new Tooltip(document.getElementById('ageFilterTooltip'));
        new Tooltip(document.getElementById('ethnicityFilterTooltip'));
        new Tooltip(document.getElementById('timePeriod2FilterTooltip'));
        new Tooltip(document.getElementById('genderChartTooltip'));
        new Tooltip(document.getElementById('ageChartTooltip'));
        new Tooltip(document.getElementById('ethnicityChartTooltip'));
        new Tooltip(document.getElementById('poaChartTooltip'));
        new Tooltip(document.getElementById('payerChartTooltip'));
    }

    initializeCustomSelects() {
        // Create mapping of element IDs to their true default values from setDefaultFilters
        const trueDefaults = {
            'stateFilter': appConstants.national,
            'ageGroupFilter': '',
            'vaccineFilter': '',
            'timePeriod2Filter': '',
            'timePeriodFilter': '',
            'mapFilter': mapType.rate,
            'geoTypeFilter': '',
            'genderFilter': 0,
            'ethnicityFilter': 0
        };
        
        // Initialize custom selects with true default values for change detection
        this.customSelectInstances = CustomSelect.initializeAll('select[data-custom-select]', {}, trueDefaults);
    }

    showChartLoadingSpinners() {
        // Show loading spinners for all charts
        showLoadingSpinner('#genderChart');
        showLoadingSpinner('#ageChart');
        showLoadingSpinner('#ethnicityChart');
        showLoadingSpinner('#poaChart');
        showLoadingSpinner('#payerChart');
    }

    setDefaultFilters() {
        if (!this.filters.state) {
            this.filters.state = appConstants.national;
        }
        if (!this.filters.map) {
            this.filters.map = mapType.rate;
        }
    }

    requiredFilterKeys = ['geoType', 'vaccine', 'ageGroup', 'timePeriod', 'timePeriod2'];
        requiredFilterLabels = {
        geoType: 'Map View',
        vaccine: 'Vaccine',
        ageGroup: 'Age',
        timePeriod: 'Year',
        timePeriod2: 'Baseline Year'
    };

    hasRequiredFilters() {
        return this.requiredFilterKeys.every(key => Number.isInteger(this.filters[key]) && this.filters[key] > 0);
    }

    updateViewMapButton() {
        const missingFilters = this.requiredFilterKeys.filter(key =>
            !Number.isInteger(this.filters[key]) || this.filters[key] <= 0
        );

        if (!this.hasAttemptedViewMap) {
            if (missingFilters.length === 0) {
                $('#viewMapHint').addClass('d-none');
            }
            return;
        }

        this.requiredFilterKeys.forEach(key => {
            const selectId = `${key}Filter`;
            const isMissing = missingFilters.includes(key);
            const $select = $(`#${selectId}`);

            $select.toggleClass('input-validation-error', isMissing);
            $select.attr('aria-invalid', isMissing ? 'true' : 'false');
            $(`label[for="${selectId}"]`).toggleClass('required-filter-error', isMissing);

            const toggle = $select.next('.custom-select').find('.custom-select__toggle')[0];
            if (toggle) {
                if (isMissing) {
                    toggle.style.setProperty('border', '2px solid #E62D47', 'important');
                } else {
                    toggle.style.removeProperty('border');
                }
            }
        });

        if (missingFilters.length > 0) {
            const labels = missingFilters.map(key => this.requiredFilterLabels[key]);
            $('#viewMapHint')
                .text(`Please select ${labels.join(', ')}.`)
                .removeClass('d-none');
        } else {
            $('#viewMapHint').addClass('d-none');
        }
    }

    populateFilters() {
        const keys = Object.keys(this.filters);
        keys.forEach(key => {
            const selector = `#${key}Filter`;
            if (this.filters[key]) {
                $(selector).val(this.filters[key]);
            }
        });
    }

    populateData() {
        this.getMapData();
        this.populateCharts();
    }

    addFilterChangeHandlers() {
        const keys = Object.keys(this.filters);
        keys.forEach(key => {
            const selector = `#${key}Filter`;
            $(selector).on('change', e => {
                const value = e.currentTarget.value;
                this.handleFilterSelect(key, value);
                // log new event
                const id = $(selector).find(`option[value="${value}"]`).text();
                this.logFilterChangeEvent(key, id);
            });
        });
    }

    addViewMapHandler() {
        $('#viewMapButton').on('click', () => {
            this.hasAttemptedViewMap = true;
            this.updateViewMapButton();

            if (!this.hasRequiredFilters()) {
                return;
            }
            $('#viewMapHint').addClass('d-none');
            this.hasViewedMap = true;
            this.applyCurrentFilters();
        });
    }
    applyCurrentFilters() {
        this.appliedFilters = { ...this.filters };
        this.updateSidebarLabel();
        this.updateMapTitle();
        this.populateData();
    }

    addResetMapHandler() {
        $('#mapResetButton').on('click', () => {
            this.updateState(null, appConstants.national);
            this.populateMap();
        });
    }

    addChartsToggleHandler() {
        $('#chartsToggleBtn').on('click', () => {
            const chartsContainer = $('.charts-container');
            const button = $('#chartsToggleBtn');
            const panelContainer = $('.vax-panel-container');
            
            if (chartsContainer.css('display') === 'none') {
                chartsContainer.css('display', 'flex');
                button.text('Close');
                button.addClass('closed');
                panelContainer.addClass('expanded');
            } else {
                chartsContainer.css('display', 'none');
                button.text('Open');
                button.removeClass('closed');
                panelContainer.removeClass('expanded');
            }
        });
    }

    handleFilterSelect(key, value) {
        const parsed = parseInt(value, 10);
        if (!Number.isInteger(parsed)) {
            return;
        }

        this.filters[key] = parsed;

        if (key === 'timePeriod' || key === 'timePeriod2') {
            this.updateTimePeriodFilters(key);
        } else if (key === 'geoType') {
            this.resetStateSelection();
        }

        this.updateViewMapButton();
        
        // Keep the initial explicit View Map action, then refresh automatically on later filter changes.
        if (this.hasViewedMap && this.hasRequiredFilters()) {
            $('#viewMapHint').addClass('d-none');
            this.applyCurrentFilters();
        }

    }

    resetStateSelection() {
        if (this.filters.state === appConstants.national) {
            return;
        }

        this.filters.state = appConstants.national;
        this.stateLabel = nationalStateLabel;
    }

    updateTimePeriodFilters(changedFilter) {
        const timePeriodSelect = $('#timePeriodFilter');
        const timePeriod2Select = $('#timePeriod2Filter');
        
        let timePeriod = parseInt(timePeriodSelect.val());
        let timePeriod2 = parseInt(timePeriod2Select.val());

        if (changedFilter === 'timePeriod' && Number.isInteger(timePeriod) && Number.isInteger(timePeriod2) && timePeriod <= timePeriod2) {
            const options = timePeriod2Select.find('option').map((_, el) => parseInt(el.value, 10)).get();
            const candidates = options.filter(val => Number.isInteger(val) && val < timePeriod);
            if (candidates.length > 0) {
                const newTimePeriod2 = Math.max(...candidates);
                timePeriod2Select.val(newTimePeriod2);
                this.filters.timePeriod2 = newTimePeriod2;
                CustomSelect.syncById('timePeriod2Filter');
            }
        } else if (changedFilter === 'timePeriod2' && Number.isInteger(timePeriod) && Number.isInteger(timePeriod2) && timePeriod2 >= timePeriod) {
            const options = timePeriodSelect.find('option').map((_, el) => parseInt(el.value, 10)).get();
            const candidates = options.filter(val => Number.isInteger(val) && val > timePeriod2);
            if (candidates.length > 0) {
                const newTimePeriod = Math.min(...candidates);
                timePeriodSelect.val(newTimePeriod);
                this.filters.timePeriod = newTimePeriod;
                CustomSelect.syncById('timePeriodFilter');
            }
        }
    }

    clearFilterUrl() {
        if (!window.location.search) {
            return;
        }

        const url = new URL(window.location.href);
        url.search = '';
        window.history.replaceState(null, null, url);
    }

    resetSelectsToDefault() {
        this.requiredFilterKeys.forEach(key => {
            this.filters[key] = 0;
            $(`#${key}Filter`).val('');
        });

        this.filters.gender = 0;
        this.filters.ethnicity = 0;
        $('#genderFilter').val('0');
        $('#ethnicityFilter').val('0');
    }

    generateFilterPayload() {
        const filters = this.appliedFilters || this.filters;
        return {
            yearId: filters.timePeriod,
            prevYearId: filters.timePeriod2,
            monthId: 0,
            vaccineId: filters.vaccine,
            stateId: filters.state,
            genderId: filters.gender,
            ageRangeId: 0,
            ageGroupId: filters.ageGroup,
            ethnicityId: filters.ethnicity,
            pointOfAccessId: 0,
            payTypeId: 0,
            projected: 0,
            month: 0
        }
    }

    populateCharts() {
        // Show loading spinners when charts are being repopulated (e.g., filter changes)
        this.showChartLoadingSpinners();
        
        this.populateByGenderChart();
        this.populateByAgeChart();
        this.populateByEthnicityChart();
        this.populateByPointOfAccessChart();
        this.populateByPayerChart();
    }

    // Helper method to render pie chart with data
    renderPieChart(containerId, data, dataPropertyName) {
        // Sort categories alphabetically; age groups by numeric order
        const sorted = sortCategoryItems(data, dataPropertyName);
        const colors = buildChartColors(sorted, dataPropertyName);
        
        // Filter out items with 0 projectedValue
        const filtered = sorted.filter(d => d.projectedValue !== 0);
        
        const chartOptions = {
            chart: {
                spacing: 0
            },
            title: {
                text: ''
            },
            plotOptions: {
                pie: {
                    innerSize: '60%'
                }
            },
            tooltip: {
                pointFormat: '{series.name}<b>{point.custom.extraInformation}</b>'
            },
            series: [{
                type: 'pie',
                name: 'Projected Value',
                data: filtered.map((d, index) => ({
                    name: d[dataPropertyName],
                    y: (d.projectedValue >= 1 && d.projectedValue <= 9) ? 10 : d.projectedValue,
                    color: colors[index],
                    custom: {
                        extraInformation: (d.projectedValue >= 1 && d.projectedValue <= 9) ? ' < 10' : ': ' + d.projectedValue.toLocaleString('en-US')
                    }
                }))
            }]
        };
        
        Highcharts.chart(containerId, chartOptions);
    }

    populateByGenderChart() {
        const url = `/api/Charts/getbygender`;
        this.requestManager.start('gender', url, this.generateFilterPayload(), (data) => {
            hideLoadingSpinner('#genderChart');
            this.renderPieChart('genderChart', data, 'gender');
        });
    }

    populateByAgeChart() {
        const url = `/api/Charts/getbyage`;
        this.requestManager.start('age', url, this.generateFilterPayload(), (data) => {
            hideLoadingSpinner('#ageChart');
            this.renderPieChart('ageChart', data, 'ageGroup');
        });
    }


    populateByEthnicityChart() {
        const url = `/api/Charts/getbyethnicity`;
        this.requestManager.start('ethnicity', url, this.generateFilterPayload(), (data) => {
            hideLoadingSpinner('#ethnicityChart');
            this.renderPieChart('ethnicityChart', data, 'ethnicity');
        });
    }

    populateByPointOfAccessChart() {
        const url = `/api/Charts/getbypointofaccess`;
        this.requestManager.start('poa', url, this.generateFilterPayload(), (data) => {
            hideLoadingSpinner('#poaChart');
            this.renderPieChart('poaChart', data, 'pointOfAccess');
        });
    }

    populateByPayerChart() {
        const url = `/api/Charts/getbypayer`;
        this.requestManager.start('payer', url, this.generateFilterPayload(), (data) => {
            hideLoadingSpinner('#payerChart');
            this.renderPieChart('payerChart', data, 'payer');
        });
    }

    getMapData() {
        const url = `/api/Map/getmap`;
        $('#mapPrompt').addClass('d-none');
        showLoadingSpinner('#map', '5rem');
        $('#totalValue').empty();
        $('#mapResetButton').addClass('d-none');
        this.requestManager.start('map', url, { ...this.generateFilterPayload(), stateId: 0 }, (data) => {
            hideLoadingSpinner('#map');
            this.mapData[geographyType.state] = data.filter(d => d.geoType === 'State');
            this.mapData[geographyType.msa] = data.filter(d => d.geoType === 'MSA');
            this.populateMap();
        });
    }

    populateMap() {
        var that = this;
        const active = this.appliedFilters || this.filters;
        const data = this.mapData[active.geoType];
        const timePeriod = active.timePeriod;
        const timePeriod2 = active.timePeriod2;

        // If there is no data, render an empty map and show the no-data message
        if (!data || data.length === 0) {
            showNoData('map');
            return;
        }

        // Common map options
        let mapOptions = {
            chart: {
                map: active.geoType === geographyType.state ? usMap : msaMap,
                spacing: 0
            },
            title: {
                text: ''
            },
            legend: {
                verticalAlign: 'bottom'
            },
            plotOptions: {
                map: {
                    nullColor: '#C7C3C3'
                }
            },
            colorAxis: {
                labels: {
                    overflow: 'allow',
                    style: {
                        fontFamily: 'Roboto, sans-serif',
                        fontSize: '14px',
                        color: '#423C3D'
                    }
                }
            },
            mapNavigation: {
                enabled: true,
                buttonOptions: {
                    padding: 0,
                    width: 37,
                    height: 60,
                    x: 0,
                    y: -40,
                    theme: { 
                        fill: 'white', 
                        r: 18,
                        stroke: '#666666',
                        'stroke-width': 0.58,
                        style: {
                            color: '#9E0059'
                        }
                    },
                    verticalAlign: 'bottom',
                    align: 'right',
                    alignTo: 'spacingBox'
                },
                buttons: {
                    zoomIn: {
                        y: -30
                    },
                    zoomOut: {
                        y: 30
                    }
                }
            },
            tooltip: {
                backgroundColor: '#FFF',
                useHTML: true,
                outside: true
            },
            series: [{
                joinBy: active.geoType === geographyType.state ? ['hc-key', 'code'] : ['name', 'code'],
                borderColor: '#FFFFFF',
                dataLabels: {
                    enabled: false
                },
                cursor: 'pointer',
                point: {
                    events: {
                        click: function () {
                            this.select();
                            const stateName = this.options.name;
                            const state = data.find(d => d.state.toLowerCase() === stateName.toLowerCase());
                            if (state) {
                                that.logFilterChangeEvent('state', state.state);
                                that.updateState(state.state, state.stateId);
                            }
                        },
                        unselect: function () {
                            return false;
                        }
                    }
                }
            }]
        }

        // Map type specific options
        switch (this.filters.map) {
            case mapType.perCapita:
                // get data range w\o outliers
                const max = data.sort((a, b) => b.percapita - a.percapita)[2];
                const min = data[data.length - 2];
                mapOptions = {
                    ...mapOptions,
                    colorAxis: {
                        ...mapOptions.colorAxis,
                        min: min.percapita,
                        max: max.percapita,
                        stops: [
                            [0, '#0B3C49'],
                            [0.2, '#0F5264'],
                            [0.4, '#147189'],
                            [0.6, '#1B96B6'],
                            [0.8, '#23C8F3'],
                            [1.0, '#ABEEFF']
                        ]
                    },
                    tooltip: {
                        ...mapOptions.tooltip,
                        formatter: function () {
                            return `
                                <h2 class="map-tooltip-header">${this.point.name}</h2>
                                <div class="map-tooltip-value">
                                    ${this.point.value.toLocaleString('en-US', { maximumFractionDigits: 0 })}
                                </div>
                                vaccinated per 100k population
                            `;
                        }
                    },
                    series: [{
                        ...mapOptions.series[0],
                        states: {
                            select: {
                                color: '#DFF4F8'
                            }
                        },
                        data: data.map(d => {
                            const code = active.geoType === geographyType.state ? this.getStateCode(d.state) : d.state;
                            return {
                                code: code,
                                id: code,
                                name: d.state,
                                value: d.percapita
                            }
                        })
                    }]
                }
                break;
            case mapType.rate:
                const nonZeroRateData = data.filter(d => d.hasData);
                const sorted = nonZeroRateData.sort((a, b) => a.percentChange - b.percentChange);
                const minPct = sorted.length > 0 ? Math.round(sorted[0].percentChange) : -100;
                const maxPct = sorted.length > 0 ? Math.round(sorted[sorted.length - 1].percentChange) : 100;
                //console.log('min: ' + minPct + ', max: ' + maxPct);

                // If there is no data, render an empty map and show the no-data message
                if (nonZeroRateData.length === 0) {
                    showNoData('map');
                    return;
                }
                
                // Calculate where zero falls in the data range (0 to 1 scale)
                const zeroPosition = (0 - minPct) / (maxPct - minPct);
                
                // Create color stops based on zero position
                let colorStops = [];
                
                if (zeroPosition <= 0) {
                    // All values are positive - use only pink colors
                    colorStops = [
                        [0.00, '#FFE5ED'],
                        [0.25, '#FFBFD4'],
                        [0.50, '#FF7FA9'],
                        [0.75, '#FF407F'],
                        [1.00, '#FF0054']
                    ];
                } else if (zeroPosition >= 1) {
                    // All values are negative - use only blue/purple colors
                    colorStops = [
                        [0.00, '#390099'],
                        [0.25, '#6B40B3'],
                        [0.50, '#9C7FCC'],
                        [0.75, '#CDBFE5'],
                        [1.00, '#EBE5F4']
                    ];
                } else {
                    // Values span both negative and positive
                    colorStops = [
                        [0.00, '#390099'],              // Most negative
                        [zeroPosition * 0.25, '#6B40B3'],
                        [zeroPosition * 0.50, '#9C7FCC'],
                        [zeroPosition * 0.75, '#CDBFE5'],
                        [zeroPosition, '#EBE5F4'],    // Zero - light purple
                        [zeroPosition, '#FFE5ED'],    // Zero - light pink
                        [zeroPosition + (1 - zeroPosition) * 0.25, '#FFBFD4'],
                        [zeroPosition + (1 - zeroPosition) * 0.50, '#FF7FA9'],
                        [zeroPosition + (1 - zeroPosition) * 0.75, '#FF407F'],
                        [1.00, '#FF0054']               // Most positive
                    ];
                }
                
                mapOptions = {
                    ...mapOptions,
                    colorAxis: {
                        ...mapOptions.colorAxis,
                        min: minPct,
                        max: maxPct,
                        startOnTick: false,  // Don't extend to nice values
                        endOnTick: false,    // Don't extend to nice values
                        showFirstLabel: true,
                        showLastLabel: true,
                        tickPositions: (() => {
                            // Handle case where min equals max
                            if (minPct === maxPct) {
                                return [minPct];
                            }
                            
                            const range = maxPct - minPct;
                            
                            // For very small ranges, use fewer ticks to avoid meaningless decimals
                            if (range <= 2) {
                                // For ranges of 2 or less, just show min and max
                                return [minPct, maxPct];
                            } else if (range <= 5) {
                                // For small ranges, use 3 ticks
                                const step = range / 2;
                                return [minPct, minPct + step, maxPct];
                            } else {
                                // For normal ranges, use 4 ticks
                                const step = range / 3;
                                return [
                                    minPct,
                                    minPct + step,
                                    minPct + (2 * step),
                                    maxPct
                                ];
                            }
                        })(),
                        stops: colorStops,
                        labels: {
                            ...mapOptions.colorAxis.labels,
                            formatter: function () {                            
                                const value = Math.round(this.value);
                                
                                // Format large numbers more compactly
                                if (Math.abs(value) >= 1000000) {
                                    return `${Math.round(value / 100000) / 10}M%`;
                                } else if (Math.abs(value) >= 1000) {
                                    return `${Math.round(value / 100) / 10}K%`;
                                } else {
                                    return `${value}%`;
                                }
                            }
                        }
                    },
                    tooltip: {
                        ...mapOptions.tooltip,
                        formatter: function () {
                            if (!this.point.hasData) {
                                return `
                                    <h2 class="map-tooltip-header">${this.point.name}</h2>
                                    <span class="map-tooltip-message">
                                        There is no data available for the selected parameters
                                    </span>
                                `;
                            }
                            const previousYearProjected = this.point.previousYearProjected >= 1 && this.point.previousYearProjected <= 9 ? '< 10' : this.point.previousYearProjected.toLocaleString('en-US');
                            const projectedValue = this.point.projectedValue >= 1 && this.point.projectedValue <= 9 ? '< 10' : this.point.projectedValue.toLocaleString('en-US');
                            var direction = this.point.value < 0 ? 'decrease' : 'increase';
                            return `
                                <h2 class="map-tooltip-header">${this.point.name}</h2>
                                <div class="map-tooltip-value">
                                    ${this.point.value.toLocaleString('en-US', { maximumFractionDigits: 0 })}%
                                </div>
                               <span class="map-tooltip-message">
                                   ${direction} from ${previousYearProjected} in ${timePeriod2} 
                                    to ${projectedValue} in ${timePeriod}
                               </span>    
                            `;
                        }
                    },
                    series: [{
                        ...mapOptions.series[0],
                        states: {
                            select: {
                                color: '#F8EAE8'
                            }
                        },
                        data: data.map(d => {
                            const code = active.geoType === geographyType.state ? this.getStateCode(d.state) : d.state;
                            return {
                                code: code,
                                id: code,
                                name: d.state,
                                value: d.percentChange,
                                projectedValue: d.projectedValue,
                                previousYearProjected: d.previousYearProjected,
                                hasData: d.hasData,
                                color: !d.hasData ? '#C7C3C3' : undefined // Use null color for 0 values
                            }
                        })
                    }]
                }
                break;
        }

        // Render the map
        const chart = Highcharts.mapChart('map', mapOptions);
        $('#mapResetButton').removeClass('d-none');

        //Load selected state, if any
        if (active.state !== 0) {
            const state = data.find(d => d.stateId === active.state);
            if (state) {
                this.updateStateLabel(state.state);
                const stateCode = active.geoType === geographyType.state ? this.getStateCode(state.state) : state.state;
                const element = chart.get(stateCode);
                if (element) {
                    element.select();
                }
            }
        }
    }

    logFilterChangeEvent(filterId, id) {
        const analytics = getAnalytics();
        logEvent(analytics, 'select_content', {
            content_type: filterId,
            item_id: id
        });
    }

    updateState(state, id) {
        if (!this.appliedFilters || this.appliedFilters.state === id) {
            return;
        }

        this.appliedFilters.state = id;
        if (this.filters.geoType === this.appliedFilters.geoType) {
            this.filters.state = id;
        }

        this.updateStateLabel(state);
        this.populateCharts();
    }

    updateStateLabel(state) {
        const label = state ? state : nationalStateLabel;
        this.stateLabel = label;
        this.updateSidebarLabel();
    }

    selectedOptionText(selectId, value) {
        return $(`#${selectId} option[value="${value}"]`).text();
    }

    updateSidebarLabel() {
        const filters = this.appliedFilters || this.filters;
        let vaccine = this.selectedOptionText('vaccineFilter', filters.vaccine);
        // If vaccine starts with Total then remove it
        if (vaccine.startsWith('Total')) {
            vaccine = vaccine.replace('Total ', '');
        }
        const location = this.stateLabel;
        const year = filters.timePeriod;
        const ageGroup = this.selectedOptionText('ageGroupFilter', filters.ageGroup);
        const gender = this.selectedOptionText('genderFilter', filters.gender);
        const ethnicity = this.selectedOptionText('ethnicityFilter', filters.ethnicity);

        let text = `Total ${vaccine} Vaccinations, ${location}, ${year}, ${ageGroup}`;

        if (filters.gender !== 0) {
            text += `, ${gender}`;
        }

        if (filters.ethnicity !== 0) {
            text += `, ${ethnicity}`;
        }

        $('#sidebarLabel').text(text);
    }

    updateMapTitle() {
        const filters = this.appliedFilters || this.filters;
        const vaccine = this.selectedOptionText('vaccineFilter', filters.vaccine);
        const title = `${vaccine} Vaccination Claims Percent Change`;
        $('#mapTitle').text(title);
    }

    getStateCode(state) {
        const stateList = {
            'Arizona': 'AZ',
            'Alabama': 'AL',
            'Alaska': 'AK',
            'Arkansas': 'AR',
            'California': 'CA',
            'Colorado': 'CO',
            'Connecticut': 'CT',
            'District of Columbia': 'DC',
            'Delaware': 'DE',
            'Florida': 'FL',
            'Georgia': 'GA',
            'Hawaii': 'HI',
            'Idaho': 'ID',
            'Illinois': 'IL',
            'Indiana': 'IN',
            'Iowa': 'IA',
            'Kansas': 'KS',
            'Kentucky': 'KY',
            'Louisiana': 'LA',
            'Maine': 'ME',
            'Maryland': 'MD',
            'Massachusetts': 'MA',
            'Michigan': 'MI',
            'Minnesota': 'MN',
            'Mississippi': 'MS',
            'Missouri': 'MO',
            'Montana': 'MT',
            'Nebraska': 'NE',
            'Nevada': 'NV',
            'New Hampshire': 'NH',
            'New Jersey': 'NJ',
            'New Mexico': 'NM',
            'New York': 'NY',
            'North Carolina': 'NC',
            'North Dakota': 'ND',
            'Ohio': 'OH',
            'Oklahoma': 'OK',
            'Oregon': 'OR',
            'Pennsylvania': 'PA',
            'Rhode Island': 'RI',
            'South Carolina': 'SC',
            'South Dakota': 'SD',
            'Tennessee': 'TN',
            'Texas': 'TX',
            'Utah': 'UT',
            'Vermont': 'VT',
            'Virginia': 'VA',
            'Washington': 'WA',
            'West Virginia': 'WV',
            'Wisconsin': 'WI',
            'Wyoming': 'WY'
        }

        const code = stateList[state];
        if (code) {
            return `us-${code.toLowerCase()}`;
        } else {
            return ''
        }
    }
}

$(() => {
    $('#mainContentLoading').addClass('d-none');
    $('#mainContent').removeClass('d-none');
    new GeographicTrendsPage();
})
